import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  Lock,
  Pencil,
  Plus,
  Redo2,
  RotateCcw,
  Trash2,
  Undo2,
  Unlock,
  X,
} from "lucide-react";
import { Button } from "./ui/Button";
import { NumberStepper } from "./ui/NumberStepper";
import { Segmented } from "./ui/Segmented";
import { cn } from "../lib/cn";
import {
  FurnitureEditorCanvas,
} from "./canvas/FurnitureEditorCanvas";
import type { SketchOutcome } from "./canvas/SketchOverlay";
import { useDesign } from "../lib/store";
import { useDraftHistory } from "../lib/draftHistory";
import { useCustomFurniture, type NewCustomFurniture } from "../lib/customFurniture";
import { FURNITURE_CATEGORIES } from "../lib/catalog";
import { denormalizeOutline, normalizeOutline } from "../lib/furnitureShape";
import { FurniturePreview } from "./FurniturePreview";
import {
  boundingBox,
  distance,
  formatLength,
  insertVertexOnEdge,
  removeVertex,
  resizeEdgeWithLocks,
} from "../lib/geom";
import type {
  CustomFurnitureItem,
  FurnitureCategory,
  FurnitureModel,
  MaterialId,
  Vec2,
} from "../lib/types";
import type { ProcessStrokeOptions } from "../lib/sketch";
import {
  DEFAULT_FREEFORM_STYLE,
  PRIMITIVES,
  addShape,
  bendEdge,
  buildFreeformParts,
  cutShape,
  frontKindForStyle,
  primitiveOutline,
  roundVertex,
  type FreeformStyle,
  type FreeformStyleKind,
  type PrimitiveKind,
} from "../lib/furniture/shapeOps";
import { MATERIALS } from "../lib/furniture/materials";

/** Small-piece thresholds so a foot-scale sketch isn't rejected as "too small"
 *  by the room-tuned `processStroke` defaults. */
const FURNITURE_SKETCH_OPTIONS: Partial<ProcessStrokeOptions> = {
  resampleSpacing: 1.5,
  minRdpToleranceIn: 0.6,
  // 0.5 ft² floor — a 10"×10" footprint still passes.
  minAreaSqIn: 0.5 * 144,
  closeThresholdIn: 4,
};

/** Default outline + dims for the color/height form before a sketch exists. */
const DEFAULT_HEIGHT_IN = 30;
const DEFAULT_COLOR = "#94a3b8";

/** A compact swatch palette covering the catalog's color families. */
const COLOR_SWATCHES = [
  "#94a3b8",
  "#4f6c8f",
  "#8a6a47",
  "#c2a17a",
  "#6e574a",
  "#3a3a44",
  "#5e8a5e",
  "#cfd2d8",
];

/** Fields copied off a placed piece so the creator can reshape that canvas copy. */
export interface PlacedShapeSeed {
  name: string;
  category: FurnitureCategory;
  width: number;
  depth: number;
  height: number;
  color: string;
  shape: Vec2[];
  model?: FurnitureModel;
  material?: CustomFurnitureItem["material"];
}

export interface FurnitureCreatorModalProps {
  onClose: () => void;
  /** When set, the modal opens in edit mode pre-seeded from this library item. */
  editingItem?: CustomFurnitureItem | null;
  /** Fired after a successful library save with the created/updated item. */
  onSaved?: (item: CustomFurnitureItem) => void;
  /**
   * When set, the form is seeded from this placed piece and save calls
   * {@link onCanvasCommit} instead of writing the library.
   */
  canvasSeed?: PlacedShapeSeed | null;
  /** Applies a canvas-mode save onto the placed piece. Does not touch the library. */
  onCanvasCommit?: (payload: NewCustomFurniture) => void;
}

type Tool = "sketch" | "shapes" | "edit";

/** Infer a starting style for an existing free-form piece from its part roles. */
function inferStyleKind(model: FurnitureModel | null | undefined): FreeformStyleKind {
  if (!model || model.kind !== "freeform") return "other";
  const roles = new Set(model.parts.map((p) => p.role));
  if (roles.has("mattress")) return "bed";
  if (roles.has("backrest") || roles.has("arm")) return roles.has("arm") && model.parts.filter((p) => p.role === "seat").length <= 1 ? "chair" : "sofa";
  if (roles.has("drawer-face") || roles.has("door-face")) return "storage";
  if (roles.has("top")) return "table";
  return "other";
}

/** Translate an outline so its bounding-box center sits on the origin. */
function centerOutline(points: Vec2[]): Vec2[] {
  const bb = boundingBox(points);
  const cx = bb.minX + bb.width / 2;
  const cy = bb.minY + bb.height / 2;
  return points.map((p) => ({ x: p.x - cx, y: p.y - cy }));
}

function withoutEdges(style: FreeformStyle): FreeformStyle {
  return { ...style, backEdges: [], armEdges: [], frontEdge: null };
}

/** Refresh locked lengths to the polygon's current edges, dropping stale indices. */
function syncedLocks(prev: Record<number, number>, pts: Vec2[]): Record<number, number> {
  const n = pts.length;
  const next: Record<number, number> = {};
  for (const [k] of Object.entries(prev)) {
    const idx = Number(k);
    if (idx < 0 || idx >= n) continue;
    const a = pts[idx];
    const b = pts[(idx + 1) % n];
    next[idx] = distance(a, b);
  }
  return next;
}

interface CreatorDraft {
  points: Vec2[];
  lockedWallLengths: Record<number, number>;
  style: FreeformStyle;
  name: string;
  category: FurnitureCategory;
  height: number;
  color: string;
  materialId: MaterialId;
}

function creatorDraftFromSeed(seed: PlacedShapeSeed | CustomFurnitureItem | null): CreatorDraft {
  return {
    points: seed ? denormalizeOutline(seed.shape, seed.width, seed.depth) : [],
    lockedWallLengths: {},
    style: { ...DEFAULT_FREEFORM_STYLE, kind: inferStyleKind(seed?.model) },
    name: seed?.name ?? "",
    category: seed?.category ?? FURNITURE_CATEGORIES[0].id,
    height: seed?.height ?? DEFAULT_HEIGHT_IN,
    color: seed?.color ?? DEFAULT_COLOR,
    materialId: seed?.material?.id ?? "matte",
  };
}

/**
 * Modal that hosts the "furniture creator" experience: sketch an outline on a
 * scaled grid (with the same orthogonal/freehand snapping, point editing, and
 * wall-length measurement locks as the room sketch), name it, pick a category +
 * height + color, and save it to the per-user custom-furniture catalog.
 *
 * Mounted only while open (the host conditionally renders it with a `key`), so
 * all local state is seeded once via `useState` initializers — no reset effect
 * needed.
 */
export function FurnitureCreatorModal({
  onClose,
  editingItem,
  onSaved,
  canvasSeed,
  onCanvasCommit,
}: FurnitureCreatorModalProps) {
  const canvasMode = canvasSeed != null;
  const seed = canvasSeed ?? editingItem ?? null;
  const unit = useDesign((s) => s.design.unit);
  const addItem = useCustomFurniture((s) => s.addItem);
  const updateItem = useCustomFurniture((s) => s.updateItem);
  const items = useCustomFurniture((s) => s.items);

  const { draft, history } = useDraftHistory(creatorDraftFromSeed(seed));
  const { points, lockedWallLengths, style, name, category, height, color, materialId } = draft;

  const [tool, setTool] = useState<Tool>(seed ? "edit" : "sketch");
  const [selectedWall, setSelectedWall] = useState<number | null>(null);
  const [selectedVertex, setSelectedVertex] = useState<number | null>(null);
  const [pendingSketch, setPendingSketch] = useState<Vec2[] | null>(null);
  const [sketchStatus, setSketchStatus] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);

  // Shapes tool: primitive stamps with add / cut. Size is a brush setting,
  // not part of the draft, so it stays off the undo stack.
  const [primitive, setPrimitive] = useState<PrimitiveKind>("rect");
  const [primW, setPrimW] = useState(24);
  const [primD, setPrimD] = useState(24);
  const [stampMode, setStampMode] = useState<"add" | "cut">("add");

  // Edit-tool refinements. The amount is a brush setting; applying it is a draft edit.
  const [cornerRadius, setCornerRadius] = useState(4);
  const [bendAmount, setBendAmount] = useState(3);

  const shapeGhost = useMemo(
    () => primitiveOutline(primitive, 0, 0, primW, primD),
    [primitive, primW, primD]
  );

  const [shapesStatus, setShapesStatus] = useState<string | null>(null);

  function handlePlaceShape(at: Vec2) {
    const shape = primitiveOutline(primitive, at.x, at.y, primW, primD);
    const result = stampMode === "cut" ? cutShape(points, shape) : addShape(points, shape);
    if (!result.ok) {
      setShapesStatus(result.message ?? "That didn't change the outline.");
      return;
    }
    setShapesStatus(null);
    history.update((d) => ({
      ...d,
      points: result.points,
      lockedWallLengths: {},
      style: withoutEdges(d.style),
    }));
    setSelectedWall(null);
    setSelectedVertex(null);
  }

  function roundSelectedCorner() {
    if (selectedVertex == null) return;
    const vertex = selectedVertex;
    history.update((d) => ({
      ...d,
      points: roundVertex(d.points, vertex, cornerRadius),
      lockedWallLengths: {},
      style: withoutEdges(d.style),
    }));
    setSelectedVertex(null);
  }

  function bendSelectedEdge(sign: 1 | -1) {
    if (selectedWall == null) return;
    const wall = selectedWall;
    history.update((d) => ({
      ...d,
      points: bendEdge(d.points, wall, sign * bendAmount),
      lockedWallLengths: {},
      style: withoutEdges(d.style),
    }));
    setSelectedWall(null);
  }

  function toggleEdgeRole(role: "back" | "arm" | "front") {
    if (selectedWall == null) return;
    const i = selectedWall;
    history.update((d) => {
      const next = d.style;
      if (role === "front") {
        return { ...d, style: { ...next, frontEdge: next.frontEdge === i ? null : i } };
      }
      const key = role === "back" ? "backEdges" : "armEdges";
      const other = role === "back" ? "armEdges" : "backEdges";
      const has = next[key].includes(i);
      return {
        ...d,
        style: {
          ...next,
          [key]: has ? next[key].filter((e) => e !== i) : [...next[key], i],
          [other]: next[other].filter((e) => e !== i),
        },
      };
    });
  }

  // Escape closes the modal. Undo/redo stay here so they don't step the room behind it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key.toLowerCase() === "z" || (e.key.toLowerCase() === "y" && !e.shiftKey))) {
        e.preventDefault();
        // The shortcut target is the window, so capture and bubble listeners
        // share one target. stopImmediatePropagation keeps the room shortcut
        // from also running.
        e.stopImmediatePropagation();
        if (e.key.toLowerCase() === "y" || e.shiftKey) history.redo();
        else history.undo();
        return;
      }
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, history]);

  const hasShape = points.length >= 3;

  const footprint = useMemo(() => {
    if (!hasShape) return null;
    const bb = boundingBox(points);
    return { width: bb.width, depth: bb.height };
  }, [points, hasShape]);

  const selectedWallLen = useMemo(() => {
    if (selectedWall == null || points.length < 2) return 0;
    const a = points[selectedWall];
    const b = points[(selectedWall + 1) % points.length];
    return distance(a, b);
  }, [selectedWall, points]);

  const selectedWallLocked =
    selectedWall != null && selectedWall in lockedWallLengths;

  useEffect(() => {
    if (selectedVertex != null && selectedVertex >= points.length) setSelectedVertex(null);
    if (selectedWall != null && (points.length < 2 || selectedWall >= points.length)) {
      setSelectedWall(null);
    }
  }, [points, selectedVertex, selectedWall]);

  function handleSketchResult(outcome: SketchOutcome) {
    if (outcome.ok && outcome.points) {
      setPendingSketch(outcome.points);
    } else {
      setPendingSketch(null);
    }
    setSketchStatus({ ok: outcome.ok, message: outcome.message });
  }

  function commitPendingSketch() {
    if (!pendingSketch) return;
    const next = pendingSketch;
    history.update((d) => ({
      ...d,
      points: next,
      lockedWallLengths: {},
      style: withoutEdges(d.style),
    }));
    setPendingSketch(null);
    setSketchStatus(null);
    setSelectedWall(null);
    setSelectedVertex(null);
    setTool("edit");
  }

  function discardPendingSketch() {
    setPendingSketch(null);
    setSketchStatus(null);
  }

  function setSelectedWallLength(newLen: number) {
    if (selectedWall == null) return;
    const wall = selectedWall;
    history.coalesce(() => {
      history.update((d) => {
        const next = resizeEdgeWithLocks(d.points, wall, newLen, d.lockedWallLengths);
        return {
          ...d,
          points: next,
          lockedWallLengths: { ...d.lockedWallLengths, [wall]: newLen },
        };
      });
    });
  }

  function toggleSelectedWallLock() {
    if (selectedWall == null) return;
    const wall = selectedWall;
    history.update((d) => {
      const next = { ...d.lockedWallLengths };
      if (wall in next) delete next[wall];
      else {
        const a = d.points[wall];
        const b = d.points[(wall + 1) % d.points.length];
        if (a && b) next[wall] = distance(a, b);
      }
      return { ...d, lockedWallLengths: next };
    });
  }

  function addCornerToSelectedWall() {
    if (selectedWall == null) return;
    const wall = selectedWall;
    history.update((d) => ({
      ...d,
      points: insertVertexOnEdge(d.points, wall, 0.5),
      style: withoutEdges(d.style),
    }));
    setSelectedWall(null);
    setSelectedVertex(wall + 1);
  }

  function removeSelectedVertex() {
    if (selectedVertex == null || points.length <= 3) return;
    const vertex = selectedVertex;
    history.update((d) => ({
      ...d,
      points: removeVertex(d.points, vertex),
      style: withoutEdges(d.style),
    }));
    setSelectedWall(null);
    setSelectedVertex(null);
  }

  function handleToolChange(next: Tool) {
    setTool(next);
    if (next !== "edit") {
      setSelectedWall(null);
      setSelectedVertex(null);
    }
    if (next !== "sketch") {
      setPendingSketch(null);
      setSketchStatus(null);
    }
  }

  function startOver() {
    history.update((d) => ({
      ...d,
      points: [],
      lockedWallLengths: {},
      style: withoutEdges(d.style),
    }));
    setPendingSketch(null);
    setSketchStatus(null);
    setSelectedWall(null);
    setSelectedVertex(null);
    setTool("sketch");
  }

  /** The parts the saved piece will carry, from the current outline + style. */
  const draftModel = useMemo<FurnitureModel | null>(() => {
    if (!hasShape) return null;
    const parts = buildFreeformParts(centerOutline(points), style, height);
    return { kind: "freeform", parts };
  }, [points, style, height, hasShape]);

  const nameTrimmed = name.trim();
  const duplicateName = useMemo(() => {
    // Canvas commits don't enter the library, so a shared name is fine —
    // a placed copy often keeps the library item's name.
    if (canvasMode || !nameTrimmed) return false;
    return items.some(
      (i) =>
        i.id !== editingItem?.id &&
        i.name.trim().toLowerCase() === nameTrimmed.toLowerCase()
    );
  }, [canvasMode, items, nameTrimmed, editingItem]);

  const canSave = hasShape && nameTrimmed.length > 0;

  function handleSave() {
    if (!canSave) return;
    const normalized = normalizeOutline(points);
    if (!normalized) return;
    const payload = {
      name: nameTrimmed,
      category,
      width: normalized.width,
      depth: normalized.depth,
      height,
      color,
      shape: normalized.shape,
      model: draftModel ?? undefined,
      material: { id: materialId, tint: color },
      frontKind: frontKindForStyle(style.kind),
    };
    if (canvasMode) {
      onCanvasCommit?.(payload);
      onClose();
      return;
    }
    if (editingItem) {
      updateItem(editingItem.id, payload);
      const updated = useCustomFurniture
        .getState()
        .items.find((i) => i.id === editingItem.id);
      if (updated) onSaved?.(updated);
    } else {
      const id = addItem(payload);
      const created = useCustomFurniture
        .getState()
        .items.find((i) => i.id === id);
      if (created) onSaved?.(created);
    }
    onClose();
  }

  return createPortal(
    <div
      data-undo-scope="creator"
      className="fixed inset-0 z-50 flex flex-col bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={canvasMode ? "Edit shape" : editingItem ? "Edit furniture" : "Create furniture"}
    >
      <div className="m-auto flex h-full w-full max-w-6xl flex-col bg-white shadow-2xl md:h-[92vh] md:my-auto md:rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-ink-100 px-4 py-3">
          <Pencil className="size-5 text-ink-700" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-ink-900 leading-tight">
              {canvasMode ? "Edit shape" : editingItem ? "Edit furniture" : "Create furniture"}
            </h2>
            <p className="text-caption text-ink-400 leading-tight">
              {canvasMode
                ? "Changes apply to this piece on the canvas."
                : "Sketch the footprint, then name and categorize it."}
            </p>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => history.undo()}
              disabled={!history.canUndo()}
              aria-label="Undo"
              data-testid="creator-undo"
              className="inline-flex size-9 items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 disabled:opacity-40 disabled:pointer-events-none"
            >
              <Undo2 className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => history.redo()}
              disabled={!history.canRedo()}
              aria-label="Redo"
              data-testid="creator-redo"
              className="inline-flex size-9 items-center justify-center rounded-xl text-ink-700 hover:bg-ink-100 disabled:opacity-40 disabled:pointer-events-none"
            >
              <Redo2 className="size-4" />
            </button>
          </div>
          <Segmented<Tool>
            options={[
              { id: "sketch", label: "Draw" },
              { id: "shapes", label: "Shapes" },
              { id: "edit", label: "Edit", disabled: !hasShape },
            ]}
            value={tool}
            onChange={handleToolChange}
            size="sm"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-1 inline-flex size-9 items-center justify-center rounded-xl text-ink-500 hover:bg-ink-100"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          {/* Canvas */}
          <div className="relative flex-1 min-h-0 bg-ink-50">
            <FurnitureEditorCanvas
              points={points}
              onChangePoints={(pts) => history.update((d) => ({ ...d, points: pts }))}
              onCommitDrag={(pts) =>
                history.update((d) => ({
                  ...d,
                  points: pts,
                  lockedWallLengths: syncedLocks(d.lockedWallLengths, pts),
                }))
              }
              onGestureStart={() => history.begin({ blockUndo: true })}
              onGestureEnd={() => history.end()}
              onGestureCancel={() => history.cancel()}
              lockedWallLengths={lockedWallLengths}
              unit={unit}
              tool={tool}
              selectedWall={selectedWall}
              selectedVertex={selectedVertex}
              onSelectWall={(i) => {
                setSelectedWall(i);
                if (i != null) setSelectedVertex(null);
              }}
              onSelectVertex={(i) => {
                setSelectedVertex(i);
                if (i != null) setSelectedWall(null);
              }}
              onSketchResult={handleSketchResult}
              previewPoints={tool === "sketch" ? pendingSketch : null}
              sketchProcessOptions={FURNITURE_SKETCH_OPTIONS}
              shapeGhost={tool === "shapes" ? shapeGhost : null}
              shapeGhostCuts={stampMode === "cut"}
              onPlaceShape={handlePlaceShape}
            />

            {/* Empty-state hint */}
            {tool === "sketch" && !hasShape && !pendingSketch && (
              <div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center px-4">
                <div className="rounded-full bg-ink-900/85 px-3 py-1.5 text-caption font-medium text-white shadow-sm">
                  Drag to draw the footprint. Hold Shift for free angles.
                </div>
              </div>
            )}
            {tool === "shapes" && (
              <div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center px-4">
                <div
                  className={cn(
                    "rounded-full px-3 py-1.5 text-caption font-medium shadow-sm",
                    shapesStatus ? "bg-rose-600 text-white" : "bg-ink-900/85 text-white"
                  )}
                  data-testid="shapes-status"
                >
                  {shapesStatus ??
                    (stampMode === "cut"
                      ? "Click to cut this shape out of the outline."
                      : hasShape
                        ? "Click to add this shape to the outline."
                        : "Click to place the first shape.")}
                </div>
              </div>
            )}

            {/* Sketch confirm bar */}
            {tool === "sketch" && (pendingSketch || sketchStatus) && (
              <div className="absolute inset-x-0 bottom-3 flex justify-center px-4">
                <div className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2 shadow-lg ring-1 ring-ink-200">
                  <span
                    className={cn(
                      "text-caption font-medium",
                      pendingSketch ? "text-ink-700" : "text-rose-600"
                    )}
                  >
                    {sketchStatus?.message ??
                      (pendingSketch ? "Looks good?" : "")}
                  </span>
                  {pendingSketch && (
                    <Button size="sm" variant="primary" onClick={commitPendingSketch}>
                      <Check className="size-4" />
                      Use this shape
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={discardPendingSketch}>
                    <RotateCcw className="size-4" />
                    Redraw
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right panel: form + selection inspector */}
          <div className="flex w-full shrink-0 flex-col gap-4 overflow-y-auto border-t border-ink-100 p-4 md:w-80 md:border-l md:border-t-0">
            {/* Shapes palette */}
            {tool === "shapes" && (
              <div className="rounded-2xl bg-ink-50 p-3 ring-1 ring-ink-100" data-testid="shapes-palette">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink-600">Shape</span>
                  <Segmented<"add" | "cut">
                    options={[
                      { id: "add", label: "Add" },
                      { id: "cut", label: "Cut", disabled: !hasShape },
                    ]}
                    value={stampMode}
                    onChange={setStampMode}
                    size="sm"
                  />
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {PRIMITIVES.map((p) => {
                    const active = p.id === primitive;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        title={p.label}
                        aria-label={p.label}
                        aria-pressed={active}
                        onClick={() => setPrimitive(p.id)}
                        className={cn(
                          "flex aspect-square items-center justify-center rounded-xl ring-1 transition-colors",
                          active ? "bg-ink-900 ring-ink-900" : "bg-white ring-ink-200 hover:bg-ink-100"
                        )}
                      >
                        <PrimitiveGlyph kind={p.id} inverted={active} />
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-end gap-3">
                  <NumberStepper label="Width" value={primW} onChange={setPrimW} history="off" min={2} max={240} step={1} suffix="in" />
                  <NumberStepper label="Depth" value={primD} onChange={setPrimD} history="off" min={2} max={240} step={1} suffix="in" />
                </div>
                <p className="mt-2 text-caption text-ink-400">
                  Shapes merge into one outline. Use Cut to carve notches, then refine corners and edges in Edit.
                </p>
              </div>
            )}

            {/* Selection inspector */}
            {tool === "edit" && selectedWall != null && (
              <div className="rounded-2xl bg-ink-50 p-3 ring-1 ring-ink-100">
                <div className="mb-2 text-xs font-semibold text-ink-600">
                  Edge {String.fromCharCode(65 + (selectedWall % 26))}
                </div>
                <div className="flex items-center gap-3">
                  <NumberStepper
                    label="Length"
                    value={Math.round(selectedWallLen)}
                    onChange={setSelectedWallLength}
                    history="off"
                    min={1}
                    max={1200}
                    step={1}
                    suffix="in"
                  />
                  <div className="flex flex-1 flex-col items-end gap-1.5">
                    <span className="text-xs text-ink-400">
                      {formatLength(selectedWallLen, unit)}
                    </span>
                    <button
                      type="button"
                      onClick={toggleSelectedWallLock}
                      aria-pressed={selectedWallLocked}
                      className={cn(
                        "inline-flex items-center gap-1 h-7 px-2 rounded-full text-caption font-semibold transition-colors",
                        selectedWallLocked
                          ? "bg-accent-600 text-white hover:bg-accent-700"
                          : "bg-ink-100 text-ink-600 hover:bg-ink-200"
                      )}
                    >
                      {selectedWallLocked ? (
                        <Lock className="size-3" />
                      ) : (
                        <Unlock className="size-3" />
                      )}
                      {selectedWallLocked ? "Locked" : "Lock"}
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={addCornerToSelectedWall}
                  >
                    <Plus className="size-4" />
                    Add corner
                  </Button>
                </div>
                {/* Bend */}
                <div className="mt-3 flex items-end gap-2">
                  <NumberStepper label="Bend" value={bendAmount} onChange={setBendAmount} history="off" min={1} max={60} step={1} suffix="in" />
                  <Button variant="secondary" size="sm" onClick={() => bendSelectedEdge(1)} title="Bow this edge outward">
                    Out
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => bendSelectedEdge(-1)} title="Bow this edge inward">
                    In
                  </Button>
                </div>
                {/* Part role for this edge */}
                {(style.kind === "sofa" || style.kind === "chair" || style.kind === "bed" || style.kind === "storage") && (
                  <div className="mt-3">
                    <div className="mb-1 text-caption font-medium text-ink-500">This edge is the…</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(style.kind === "storage"
                        ? ([["front", "Drawer front"]] as const)
                        : style.kind === "bed"
                          ? ([["back", "Headboard"]] as const)
                          : ([["back", "Back"], ["arm", "Arm"]] as const)
                      ).map(([role, label]) => {
                        const active =
                          role === "front"
                            ? style.frontEdge === selectedWall
                            : role === "back"
                              ? style.backEdges.includes(selectedWall)
                              : style.armEdges.includes(selectedWall);
                        return (
                          <button
                            key={role}
                            type="button"
                            aria-pressed={active}
                            onClick={() => toggleEdgeRole(role)}
                            data-testid={`edge-role-${role}`}
                            className={cn(
                              "h-8 rounded-full px-3 text-xs font-medium transition-colors",
                              active ? "bg-accent-600 text-white" : "bg-white text-ink-600 ring-1 ring-ink-200 hover:bg-ink-100"
                            )}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tool === "edit" && selectedVertex != null && (
              <div className="rounded-2xl bg-ink-50 p-3 ring-1 ring-ink-100">
                <div className="mb-2 text-xs font-semibold text-ink-600">
                  Corner {selectedVertex + 1}
                </div>
                <p className="mb-2 text-caption text-ink-500">
                  Drag the highlighted corner to reshape. Hold Shift to lock to
                  an axis.
                </p>
                <div className="mb-3 flex items-end gap-2">
                  <NumberStepper label="Round" value={cornerRadius} onChange={setCornerRadius} history="off" min={1} max={60} step={1} suffix="in" />
                  <Button variant="secondary" size="sm" onClick={roundSelectedCorner} data-testid="round-corner-button">
                    Round corner
                  </Button>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={removeSelectedVertex}
                  disabled={points.length <= 3}
                >
                  <Trash2 className="size-4" />
                  Remove corner
                </Button>
              </div>
            )}

            {/* Style / parts */}
            {hasShape && (
              <div className="flex flex-col gap-1.5" data-testid="freeform-style">
                <span className="text-xs font-medium text-ink-500">What is it?</span>
                <div className="flex flex-wrap gap-1.5">
                  {(
                    [
                      ["other", "Other"],
                      ["sofa", "Sofa"],
                      ["chair", "Chair"],
                      ["table", "Table"],
                      ["storage", "Storage"],
                      ["bed", "Bed"],
                      ["rug", "Rug"],
                    ] as const
                  ).map(([id, label]) => {
                    const active = style.kind === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => history.update((d) => ({ ...d, style: { ...d.style, kind: id } }))}
                        className={cn(
                          "h-8 rounded-full px-3 text-xs font-medium transition-colors",
                          active ? "bg-ink-900 text-white" : "bg-ink-50 text-ink-600 ring-1 ring-ink-100 hover:bg-ink-100"
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {(style.kind === "sofa" || style.kind === "chair" || style.kind === "bed" || style.kind === "storage") && (
                  <p className="text-caption text-ink-400">
                    {tool === "edit"
                      ? style.kind === "storage"
                        ? "Select an edge and mark it as the drawer front."
                        : style.kind === "bed"
                          ? "Select an edge and mark it as the headboard."
                          : "Select edges and mark them as the back or arms — cushions fill the rest."
                      : "Switch to Edit to mark which edges are the back, arms, or front."}
                  </p>
                )}
              </div>
            )}

            {/* Name */}
            <label className="flex flex-col gap-1">
              <span
                className={cn(
                  "text-xs font-medium",
                  hasShape && !nameTrimmed ? "text-rose-600" : "text-ink-500"
                )}
              >
                {hasShape && !nameTrimmed ? "Name (required)" : "Name"}
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => history.update((d) => ({ ...d, name: e.target.value }))}
                onFocus={() => history.begin()}
                onBlur={() => history.end()}
                placeholder="e.g. Sectional sofa"
                data-testid="furniture-creator-name"
                className={cn(
                  "h-11 rounded-xl bg-white px-3 text-sm ring-1 outline-none focus:ring-2 focus:ring-accent-500/40",
                  hasShape && !nameTrimmed
                    ? "ring-rose-300"
                    : "ring-ink-200"
                )}
              />
              {duplicateName && (
                <span className="text-caption text-amber-600">
                  You already have a piece with this name.
                </span>
              )}
            </label>

            {/* Category */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-500">Category</span>
              <div className="flex flex-wrap gap-1.5">
                {FURNITURE_CATEGORIES.map((c) => {
                  const active = c.id === category;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => history.update((d) => ({ ...d, category: c.id }))}
                      className={cn(
                        "h-9 rounded-full px-3 text-xs font-medium transition-colors",
                        active
                          ? "bg-ink-900 text-white"
                          : "bg-ink-50 text-ink-600 ring-1 ring-ink-100 hover:bg-ink-100"
                      )}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Height + footprint readout */}
            <div className="flex items-end gap-3">
              <NumberStepper
                label="Height"
                value={Math.round(height)}
                onChange={(v) => history.coalesce(() => history.update((d) => ({ ...d, height: v })))}
                history="off"
                min={1}
                max={600}
                step={1}
                suffix="in"
              />
              <div className="flex flex-1 flex-col items-end gap-0.5 pb-1 text-right">
                <span className="text-caption text-ink-400">Footprint</span>
                <span className="text-xs font-medium text-ink-700 tabular-nums">
                  {footprint
                    ? `${formatLength(footprint.width, unit)} × ${formatLength(
                        footprint.depth,
                        unit
                      )}`
                    : "—"}
                </span>
              </div>
            </div>

            {/* Material */}
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-500">Material</span>
              <select
                value={materialId}
                onChange={(e) => history.update((d) => ({ ...d, materialId: e.target.value as MaterialId }))}
                data-testid="furniture-creator-material"
                className="h-10 rounded-xl bg-white px-3 text-sm ring-1 ring-ink-200 outline-none focus:ring-2 focus:ring-accent-500/40"
              >
                {MATERIALS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>

            {/* Color */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-ink-500">Color</span>
              <div className="flex flex-wrap items-center gap-2">
                {COLOR_SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Color ${c}`}
                    onClick={() => history.update((d) => ({ ...d, color: c }))}
                    className={cn(
                      "size-7 rounded-full ring-1 ring-black/10 transition-transform",
                      color === c
                        ? "ring-2 ring-accent-500 scale-110"
                        : "hover:scale-105"
                    )}
                    style={{ background: c }}
                  />
                ))}
                <label className="ml-1 inline-flex size-7 cursor-pointer items-center justify-center rounded-full ring-1 ring-ink-200">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => history.update((d) => ({ ...d, color: e.target.value }))}
                    onFocus={() => history.begin()}
                    onBlur={() => history.end()}
                    className="size-7 cursor-pointer rounded-full opacity-0"
                    aria-label="Custom color"
                  />
                  <span
                    className="pointer-events-none absolute size-4 rounded-full"
                    style={{ background: color }}
                  />
                </label>
              </div>
            </div>

            {/* Live preview */}
            <ShapePreview
              points={points}
              color={color}
              height={height}
              name={nameTrimmed || "Custom piece"}
              model={draftModel}
              materialId={materialId}
            />

            <div className="flex-1" />

            {/* Footer actions */}
            <div className="flex items-center gap-2 pt-1">
              {hasShape && (
                <Button variant="ghost" size="sm" onClick={startOver}>
                  <RotateCcw className="size-4" />
                  Start over
                </Button>
              )}
              <div className="flex-1" />
              <Button variant="secondary" size="md" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSave}
                disabled={!canSave}
                data-testid="furniture-creator-save"
              >
                {canvasMode ? "Update piece" : editingItem ? "Save changes" : "Add to library"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** A live preview of the sketched outline rendered exactly as the canvas will draw it. */
function ShapePreview({
  points,
  color,
  height,
  name,
  model,
  materialId,
}: {
  points: Vec2[];
  color: string;
  height: number;
  name: string;
  model: FurnitureModel | null;
  materialId: MaterialId;
}) {
  const preview = useMemo(() => {
    const normalized = normalizeOutline(points);
    if (!normalized) return null;
    return {
      name,
      width: normalized.width,
      depth: normalized.depth,
      height,
      color,
      shape: normalized.shape,
      model: model ?? undefined,
      material: { id: materialId, tint: color },
    };
  }, [points, color, height, name, model, materialId]);

  if (!preview) return null;
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-ink-50 p-3 ring-1 ring-ink-100">
      <FurniturePreview furniture={preview} size={96} pad={6} className="shrink-0" />
      <div className="text-caption text-ink-500">
        This is how the piece will appear on your room canvas.
      </div>
    </div>
  );
}

/** Tiny inline glyph for a primitive button. */
function PrimitiveGlyph({ kind, inverted }: { kind: PrimitiveKind; inverted: boolean }) {
  const stroke = inverted ? "#ffffff" : "#3a3f4a";
  const fill = inverted ? "rgba(255,255,255,0.18)" : "rgba(58,63,74,0.12)";
  switch (kind) {
    case "rect":
      return (
        <svg viewBox="0 0 24 24" className="size-5">
          <rect x="4" y="6" width="16" height="12" fill={fill} stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    case "rounded":
      return (
        <svg viewBox="0 0 24 24" className="size-5">
          <rect x="4" y="6" width="16" height="12" rx="4" fill={fill} stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    case "circle":
      return (
        <svg viewBox="0 0 24 24" className="size-5">
          <circle cx="12" cy="12" r="7.5" fill={fill} stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    case "ellipse":
      return (
        <svg viewBox="0 0 24 24" className="size-5">
          <ellipse cx="12" cy="12" rx="8.5" ry="5.5" fill={fill} stroke={stroke} strokeWidth="1.5" />
        </svg>
      );
    case "quarter":
      return (
        <svg viewBox="0 0 24 24" className="size-5">
          <path d="M5 5 H19 A14 14 0 0 1 5 19 Z" fill={fill} stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      );
  }
}
